import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Slider,
  Typography,
} from "@mui/material";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import IdList from "./IdList";
import {
  bisectLeft,
  bisectRight,
  formatDateTime,
  formatTime,
  minMaxDownsample,
  timeTicks,
  windowLabel,
} from "./harpieUtils";

const FLOW_COLOR = "#673ab7";
// Points across all records on the chart. A day of one sensor is ~13,000
// samples, far more than the chart has pixels; thinned (keeping each bucket's
// extremes) it looks the same and redraws quickly.
const POINT_BUDGET = 3000;
// Clicking a point zooms in this much around it, down to the narrowest window.
const ZOOM_FACTOR = 4;
const MIN_ZOOM_SPAN = 30;
// Below this many points on screen, each sample gets a marker.
const MARKER_LIMIT = 300;
const NO_RECORDS = [];

// AL/Dbg/Flow records by sensor ID, graphed on the dtime clock.
// records: the query's flow records, newest first.
function FlowEventsTab({ records, loaded }) {
  const bySensor = useMemo(() => {
    const groups = new Map();
    for (const record of records) {
      if (!groups.has(record.sensorId)) groups.set(record.sensorId, []);
      groups.get(record.sensorId).push(record);
    }
    return groups;
  }, [records]);

  const [selectedSensor, setSelectedSensor] = useState(null);
  // null: every record of the sensor, in sequence.
  const [record, setRecord] = useState(null);
  // [lo, hi] epoch seconds zoomed to, null for all of it.
  const [zoom, setZoom] = useState(null);
  // The range slider's thumbs while dragged; the chart waits for the release.
  const [dragging, setDragging] = useState(null);

  // The previous sensor if the new results still have it, else the first.
  const sensor = bySensor.has(selectedSensor) ? selectedSensor : bySensor.keys().next().value ?? null;
  const sensorRecords = bySensor.get(sensor) || NO_RECORDS;

  useEffect(() => {
    setRecord(null);
    setZoom(null);
  }, [records]);

  const selectSensor = (id) => {
    setSelectedSensor(id);
    setRecord(null);
    setZoom(null);
  };

  const selectRecord = (r) => {
    setRecord(r);
    setZoom(null);
  };

  // The records on the chart, oldest first, and the epoch span of their samples.
  const shown = useMemo(
    () =>
      (record ? [record] : sensorRecords)
        .filter((r) => r.times.length)
        .sort((a, b) => a.timestamp.localeCompare(b.timestamp)),
    [record, sensorRecords]
  );
  const extent = shown.length
    ? [Math.min(...shown.map((r) => r.times[0])), Math.max(...shown.map((r) => r.times[r.times.length - 1]))]
    : null;
  const [lo, hi] = zoom || extent || [0, 1];

  // Only the samples in the window are drawn, the point budget shared out by
  // record length, so zooming in brings back the samples thinning dropped. A
  // null between records breaks the line instead of joining across the gap.
  const { data, pointCount } = useMemo(() => {
    const visible = [];
    for (const r of shown) {
      const i = bisectLeft(r.times, lo);
      const j = bisectRight(r.times, hi);
      if (i < j) visible.push([r.times.slice(i, j), r.values.slice(i, j)]);
    }
    const total = visible.reduce((sum, [times]) => sum + times.length, 0);
    const points = [];
    let count = 0;
    for (const [times, values] of visible) {
      const buckets = Math.max(1, Math.floor((POINT_BUDGET * times.length) / (2 * total)));
      const [xs, ys] = minMaxDownsample(times, values, buckets);
      xs.forEach((t, k) => points.push({ t, v: ys[k] }));
      points.push({ t: xs[xs.length - 1], v: null });
      count += xs.length;
    }
    return { data: points, pointCount: count };
  }, [shown, lo, hi]);

  const zoomTo = (newLo, newHi) => {
    if (!extent) return;
    const [first, last] = extent;
    if (newHi - newLo < MIN_ZOOM_SPAN) {
      const middle = (newLo + newHi) / 2;
      newLo = middle - MIN_ZOOM_SPAN / 2;
      newHi = middle + MIN_ZOOM_SPAN / 2;
    }
    if (newHi > last) {
      newLo -= newHi - last;
      newHi = last;
    }
    if (newLo < first) {
      newHi = Math.min(newHi + (first - newLo), last);
      newLo = first;
    }
    setZoom(newLo === first && newHi === last ? null : [newLo, newHi]);
  };

  const zoomAt = (t) => {
    const half = (hi - lo) / ZOOM_FACTOR / 2;
    zoomTo(t - half, t + half);
  };

  const { ticks, format } = timeTicks(lo, hi - lo);
  const span = extent ? Math.max(extent[1] - extent[0], 1) : 1;
  const sliderValue = dragging || (extent ? [lo - extent[0], hi - extent[0]] : [0, 1]);

  let title = "No flow records";
  let subtitle = "";
  let details = "";
  if (sensorRecords.length && !record) {
    title = sensor;
    subtitle = `All ${sensorRecords.length} records in sequence, gateway ${sensorRecords[0].gatewayId}`;
    details = "Select a record for its header.";
  } else if (record) {
    title = sensor;
    subtitle = `Record received ${formatDateTime(record.timestamp)} UTC`;
    details = describe(record);
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4} lg={3}>
        <IdList
          title="Sensor IDs"
          loaded={loaded}
          items={[...bySensor].map(([id, rs]) => ({
            id,
            subtitle: `${rs.length} records · ${rs.reduce((n, r) => n + r.values.length, 0)} samples · gateway ${rs[0].gatewayId}`,
          }))}
          selected={sensor}
          onSelect={selectSensor}
          emptyText="No AL/Dbg/Flow events in this window."
        />
        <Divider />
        <Typography variant="h6">Records</Typography>
        <List dense sx={{ maxHeight: 300, overflow: "auto" }}>
          {sensorRecords.length ? (
            <ListItemButton selected={record === null} onClick={() => selectRecord(null)}>
              <ListItemText primary="All records in sequence" />
            </ListItemButton>
          ) : null}
          {sensorRecords.map((r) => (
            <ListItemButton
              key={r.timestamp}
              selected={record === r}
              onClick={() => selectRecord(r)}
            >
              <ListItemText
                primary={formatDateTime(r.timestamp)}
                secondary={`${r.chunks.length} chunks · ${r.values.length} samples · every ${r.interval} s`}
                secondaryTypographyProps={{ fontSize: 11 }}
              />
            </ListItemButton>
          ))}
        </List>
      </Grid>
      <Grid item xs={12} md={8} lg={9}>
        <Typography variant="h6" sx={{ fontFamily: "monospace" }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
        <ResponsiveContainer width="100%" height={450}>
          <LineChart
            data={data}
            margin={{ top: 10, right: 30, left: 20, bottom: 20 }}
            onClick={(state) => {
              if (state && state.activeLabel !== undefined) zoomAt(state.activeLabel);
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="t"
              type="number"
              domain={[lo, hi]}
              allowDataOverflow
              ticks={ticks}
              tickFormatter={format}
              label={{ value: "Sample time, UTC (dtime + k × interval)", position: "insideBottom", offset: -10 }}
            />
            <YAxis
              domain={["auto", "auto"]}
              label={{ value: "Flow data", angle: -90, position: "insideLeft" }}
            />
            <Tooltip
              labelFormatter={(t) => `${formatTime(t)} UTC`}
              formatter={(v) => [v, "Flow"]}
            />
            <Line
              type="linear"
              dataKey="v"
              stroke={FLOW_COLOR}
              strokeWidth={2}
              dot={pointCount <= MARKER_LIMIT}
              isAnimationActive={false}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
        {/* height: auto overrides main.css's .MuiBox-root { height: 100% }. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 2, height: "auto" }}>
          <Slider
            value={sliderValue}
            min={0}
            max={span}
            step={1}
            disabled={!extent || span <= MIN_ZOOM_SPAN}
            onChange={(e, value) => setDragging(value)}
            onChangeCommitted={(e, value) => {
              setDragging(null);
              zoomTo(extent[0] + value[0], extent[0] + value[1]);
            }}
            sx={{ flexGrow: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {extent
              ? dragging
                ? windowLabel(extent[0] + dragging[0], extent[0] + dragging[1])
                : `${windowLabel(lo, hi)} · ${zoom ? "zoomed" : "drag the ends, or click a point to zoom in"}`
              : ""}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ZoomOutMapIcon />}
            disabled={!zoom}
            onClick={() => setZoom(null)}
          >
            Reset zoom
          </Button>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box
          component="pre"
          sx={{ fontSize: 11, height: 130, overflow: "auto", m: 0, userSelect: "text" }}
        >
          {details}
        </Box>
      </Grid>
    </Grid>
  );
}

function describe(record) {
  const summary = {
    "gateway coreID": record.gatewayId,
    "received (earliest chunk)": `${formatDateTime(record.timestamp)} UTC`,
    "first sample (dtime)": record.times.length ? `${formatDateTime(record.times[0])} UTC` : null,
    chunks: record.chunks.map((c) => ({
      received: formatTime(c.received),
      dtime: c.dtime,
      samples: c.samples,
    })),
    header: record.header,
  };
  return JSON.stringify(summary, null, 1);
}

export default FlowEventsTab;
